import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Spinner } from "@/components/ui/spinner";
import NotFound from "@/pages/NotFound";
import { lazy, Suspense } from "react";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import DevDiagnostics from "./components/DevDiagnostics";
import { ConfirmProvider } from "./components/ConfirmProvider";

// Route-based code splitting: each page becomes its own lazily-loaded chunk so
// the initial bundle only ships the shell + the first route the user hits.
const Login = lazy(() => import("./pages/Login"));
const Home = lazy(() => import("./pages/Home"));
const AdminUsersRoles = lazy(() => import("./pages/AdminUsersRoles"));
const AdminTeams = lazy(() => import("./pages/AdminTeams"));
const LeadDetail = lazy(() => import("./pages/LeadDetail"));
const LeadsMyList = lazy(() => import("./pages/LeadsMyList"));
const LeadsAllList = lazy(() => import("./pages/LeadsAllList"));
const LeadsKanban = lazy(() => import("./pages/LeadsKanban"));
const LeadPool = lazy(() => import("./pages/LeadPool"));
const LeadCreate = lazy(() => import("./pages/LeadCreate"));
const ReferralsList = lazy(() => import("./pages/ReferralsList"));
const ReferralDetail = lazy(() => import("./pages/ReferralDetail"));
const Locations = lazy(() => import("./pages/Locations"));
const Properties = lazy(() => import("./pages/Properties"));
const SystemSettings = lazy(() => import("./pages/SystemSettings"));
const LeadSources = lazy(() => import("./pages/LeadSources"));
const TemporaryLeads = lazy(() => import("./pages/TemporaryLeads"));
const RotationsList = lazy(() => import("./pages/RotationsList"));
const RotationDetail = lazy(() => import("./pages/RotationDetail"));
const BayutLocations = lazy(() => import("./pages/BayutLocations"));
const PfLocations = lazy(() => import("./pages/PfLocations"));
const TwilioSettings = lazy(() => import("./pages/TwilioSettings"));
const TwilioTemplates = lazy(() => import("./pages/TwilioTemplates"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const CampaignAudience = lazy(() => import("./pages/CampaignAudience"));
const DevelopersPage = lazy(() => import("./pages/developers"));
const PfUsers = lazy(() => import("./pages/pfUsers"));
const Views = lazy(() => import("./pages/Views"));
const PropertyTypes = lazy(() => import("./pages/PropertyTypes"));
const Persons = lazy(() => import("./pages/Persons"));
const ListingsAll = lazy(() => import("@/pages/ListingsAll"));
const ListingCreate = lazy(() => import("@/pages/ListingCreate"));
const ListingView = lazy(() => import("@/pages/ListingView"));
const ListingEdit = lazy(() => import("@/pages/ListingEdit"));
const ListingsMy = lazy(() => import("@/pages/ListingsMy"));
const ListingsManagement = lazy(() => import("@/pages/ListingsManagement"));
const AgentSlabs = lazy(() => import("./pages/AgentSlabs"));
const AllowedIps = lazy(() => import("./pages/AllowedIps"));
const GoogleReviews = lazy(() => import("./pages/GoogleReviews"));
const MyGoogleReviews = lazy(() => import("./pages/MyGoogleReviews"));
const MyMeetings = lazy(() => import("./pages/MyMeetings"));
const TeamMeetings = lazy(() => import("./pages/TeamMeetings"));
const KpiTargets = lazy(() => import("./pages/KpiTargets"));
const KpiWeights = lazy(() => import("./pages/KpiWeights"));
const TeamKpiCategory = lazy(() => import("./pages/TeamKpiCategory"));
const MyDealsList = lazy(() => import("@/pages/MyDealsList"));
const DealDetail = lazy(() => import("@/pages/DealDetail"));
const MyCommissionsList = lazy(() => import("@/pages/MyCommissionsList"));
const CompanyDealsList = lazy(() => import("@/pages/CompanyDealsList"));
const CeoPendingApprovals = lazy(() => import("@/pages/CeoPendingApprovals"));
const CompanyPerformance = lazy(() => import("@/pages/CompanyPerformance"));
const MyAttendance = lazy(() => import("@/pages/MyAttendance"));
const TeamAttendance = lazy(() => import("@/pages/TeamAttendance"));
const AdminReshuffleExcludedLocations = lazy(() => import("@/pages/AdminReshuffleExcludedLocations"));
const TeamKpiPage = lazy(() => import("@/pages/TeamKpiPage"));
const MissedCalls = lazy(() => import("./pages/MissedCalls"));

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Spinner className="size-6 text-muted-foreground" />
    </div>
  );
}

function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <Suspense fallback={<RouteFallback />}>
      <Switch>
        <Route path={"/login"} component={Login} />
        <Route path={"/"} component={Home} />
        <Route path={"/admin/users"} component={AdminUsersRoles} />
        <Route path={"/admin/teams"} component={AdminTeams} />
        <Route path={"/leads/my"} component={LeadsMyList} />
        <Route path={"/leads/all"} component={LeadsAllList} />
        <Route path={"/leads/pool"} component={LeadPool} />
        <Route path={"/leads/temporary"} component={TemporaryLeads} />
        <Route path={"/leads/kanban"} component={LeadsKanban} />
        <Route path={"/leads/create"} component={LeadCreate} />
        <Route path={"/leads/:id"} component={LeadDetail} />
        <Route path={"/referrals"} component={ReferralsList} />
        <Route path={"/referrals/:id"} component={ReferralDetail} />
        <Route path={"/admin/locations"} component={Locations} />
        <Route path={"/admin/settings"} component={SystemSettings} />
        <Route path={"/admin/lead-sources"} component={LeadSources} />
        <Route path={"/admin/bayut-locations"} component={BayutLocations} />
        <Route path={"/admin/pf-locations"} component={PfLocations} />
        <Route path={"/rotations"} component={RotationsList} />
        <Route path={"/rotations/:id"} component={RotationDetail} />
        <Route path={"/admin/properties"} component={Properties} />
        <Route path={"/404"} component={NotFound} />
        <Route path={"/admin/twilio-settings"} component={TwilioSettings} />
        <Route path={"/admin/twilio-templates"} component={TwilioTemplates} />
        <Route path="/admin/campaigns" component={Campaigns} />
        <Route path="/admin/campaign-audience" component={CampaignAudience} />
        <Route path="/admin/developers" component={DevelopersPage} />
        <Route path="/admin/pf-users" component={PfUsers} />
        <Route path="/admin/views" component={Views} />
        <Route path="/admin/property-types" component={PropertyTypes} />
        <Route path="/admin/persons" component={Persons} />
        <Route path="/listings/my-listings" component={ListingsMy} />
        <Route path="/listings/all" component={ListingsAll} />
        <Route path="/listings/listing-management" component={ListingsManagement} />
        <Route path="/listings/create" component={ListingCreate} />
        <Route path="/listings/:id" component={ListingView} />
        <Route path="/listings/:id/edit" component={ListingEdit} />
        <Route path={"/admin/agent-slabs"} component={AgentSlabs} />
        <Route path={"/admin/allowed-ips"} component={AllowedIps} />
        <Route path={"/google-reviews"} component={GoogleReviews} />
        <Route path={"/my-google-reviews"} component={MyGoogleReviews} />
        <Route path={"/my-meetings"} component={MyMeetings} />
        <Route path={"/team-meetings"} component={TeamMeetings} />
        <Route path={"/kpi-settings/targets"} component={KpiTargets} />
        <Route path={"/kpi-settings/weights"} component={KpiWeights} />
        <Route path={"/kpi-settings/teams"} component={TeamKpiCategory} />
        <Route path={"/kpi-settings/teamskpis"} component={TeamKpiPage} />
        <Route path="/deals/my" component={MyDealsList} />
        <Route path="/deals/all" component={CompanyDealsList} />
        <Route path="/deals/ceo-queue" component={CeoPendingApprovals} />
        <Route path="/deals/performance" component={CompanyPerformance} />
        <Route path="/deals/:id" component={DealDetail} />
        <Route path="/commissions/my" component={MyCommissionsList} />
        <Route path="/attendance/my" component={MyAttendance} />
        <Route path="/attendance/team" component={TeamAttendance} />
        <Route path="/missed-calls" component={MissedCalls} />
        <Route path="/operations/missed-calls" component={MissedCalls} />
        <Route path="/admin/reshuffle-exclusions" component={AdminReshuffleExcludedLocations} />

        {/* Final fallback route */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function App() {
  return (
    <ErrorBoundary>
      <DevDiagnostics />
      <ThemeProvider
        defaultTheme="light"
        // switchable
      >
        <TooltipProvider>
          <Toaster />
          <ConfirmProvider>
            <Router />
          </ConfirmProvider>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
